import { useFormContext } from 'react-hook-form';
// Types
import { FormInputs } from 'components/CreatorSetup/StreamingSetup/CreateStreamSetup/CreateStreamSetup';
// Hooks
import { useEstimatedStreamProfit } from 'hooks';
import { useTicketingLaunched } from 'hooks/useTicketingLaunched';
// Helpers
import { calculateAffiliateCommission, roundMoney } from 'helpers/affiliate';
import { getTicketTiersSummary } from 'helpers/ticketEvents';
import { formatCurrencyStringWithoutName } from 'helpers/formatCurrencyString';
import { formatPriceToUSD } from 'helpers/priceText';
// Components
import SummaryCard from 'components/common3/SummaryCard/SummaryCard';

const EMPTY = '—';

/**
 * Right-hand "SUMMARY" card of the create wizard. Every amount is the
 * organizer's net (after the platform fee), the same base the backend uses to
 * snapshot the commission on each order.
 */
const StreamSummaryPanel = (): JSX.Element => {
  const { watch } = useFormContext<FormInputs>();
  const ticketingLaunched = useTicketingLaunched();

  const isFree = Boolean(watch('isFree'));
  const price = Number(watch('price'));
  const hasPrice = !isFree && price > 0;
  const netProfit = useEstimatedStreamProfit(price, isFree);

  const affiliateEnabled = Boolean(watch('affiliateEnabled')) && !isFree;
  const commissionPercent = Number(watch('affiliateCommissionPercent'));
  const hasCommission = affiliateEnabled && commissionPercent > 0;
  const participantEarns = calculateAffiliateCommission(
    netProfit,
    commissionPercent
  );
  const organizerKeeps = roundMoney(netProfit - participantEarns);

  // A stream sells tickets only while its Event Tickets switch is on.
  const sellsTickets = Boolean(watch('ticketsEnabled')) && !isFree;
  const tiers = (sellsTickets && watch('tiers')) || [];
  const {
    count: tierCount,
    ticketsAvailable,
    maxRevenue,
  } = getTicketTiersSummary(tiers);

  const streamPrice = isFree
    ? 'Free Stream'
    : hasPrice
    ? formatPriceToUSD(price)
    : EMPTY;
  const totalPerSale = !hasPrice
    ? EMPTY
    : hasCommission
    ? `${formatPriceToUSD(organizerKeeps)} / affiliate sale`
    : formatPriceToUSD(netProfit);

  return (
    <SummaryCard
      groups={[
        [
          { label: 'Stream Price', value: streamPrice },
          {
            label: 'Affiliate program',
            value: affiliateEnabled ? 'On' : 'Off',
          },
          {
            label: 'Commission',
            value: hasCommission ? `${commissionPercent}%` : EMPTY,
          },
          {
            label: 'Participant earns',
            value: hasCommission
              ? `${formatPriceToUSD(participantEarns)} / sale`
              : EMPTY,
          },
          { label: 'Total per sale', value: totalPerSale },
        ],
        ...(ticketingLaunched
          ? [
              [
                { label: 'Ticketing', value: sellsTickets ? 'On' : 'Off' },
                ...(sellsTickets
                  ? [
                      { label: 'Tiers', value: tierCount },
                      { label: 'Tickets available', value: ticketsAvailable },
                      {
                        label: 'Max ticket revenue',
                        value: formatCurrencyStringWithoutName(maxRevenue),
                      },
                    ]
                  : []),
              ],
            ]
          : []),
      ]}
    />
  );
};

export default StreamSummaryPanel;
